import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { listInventory, setInventory, deleteInventory, listProducts } from '@vn-propane/shared/client';
import type { InventoryWithProduct, Product, ErrorResponse } from '@vn-propane/shared/client';
import { Plus, Package, Trash2, Pencil, Minus } from 'lucide-react';
import { useGasStation } from '../GasStationsLayout';
import { formatPrice } from '@/lib/utils';

export default function GasStationInventory() {
  const { t } = useTranslation(['admin', 'common']);
  const { id: orgId, stationId } = useParams<{ id: string; stationId: string }>();
  const { station, organization } = useGasStation();
  const [inventory, setInventoryState] = useState<InventoryWithProduct[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add product dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [addQuantity, setAddQuantity] = useState('');
  const [addMaxCapacity, setAddMaxCapacity] = useState('');
  const [addAlertThreshold, setAddAlertThreshold] = useState('');
  const [adding, setAdding] = useState(false);

  // Edit dialog
  const [editItem, setEditItem] = useState<InventoryWithProduct | null>(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editCapacity, setEditCapacity] = useState('');
  const [editAlert, setEditAlert] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteItem, setDeleteItem] = useState<InventoryWithProduct | null>(null);

  const fetchInventory = useCallback(async () => {
    if (!orgId || !stationId) return;

    try {
      const result = await listInventory({
        path: { org_id: orgId, station_id: stationId },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('errors:failedToLoad', { resource: t('inventory.title') }));
        return;
      }

      if (result.data) {
        setInventoryState(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('errors:failedToLoad', { resource: t('inventory.title') }));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, stationId, t]);

  const fetchProducts = useCallback(async () => {
    if (!orgId) return;

    try {
      const result = await listProducts({
        path: { org_id: orgId },
      });

      if (result.data) {
        setProducts(result.data.data);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchInventory();
    fetchProducts();
  }, [fetchInventory, fetchProducts]);

  const formatQuantity = (quantity: string) => {
    const num = parseFloat(quantity);
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  // Products not yet in inventory
  const availableProducts = products.filter(
    (p) => !inventory.some((i) => i.product_id === p.id)
  );

  const handleAddProduct = async () => {
    if (!orgId || !stationId || !selectedProduct || !addQuantity) return;

    setAdding(true);
    try {
      const result = await setInventory({
        path: { org_id: orgId, station_id: stationId },
        body: {
          product_id: selectedProduct.id,
          quantity: addQuantity,
          max_capacity: addMaxCapacity || null,
          alert_threshold: addAlertThreshold || null,
        },
      });

      if (result.error) {
        console.error('Failed to add product:', result.error);
        return;
      }

      await fetchInventory();
      setIsAddOpen(false);
      setSelectedProduct(null);
      setAddQuantity('');
      setAddMaxCapacity('');
      setAddAlertThreshold('');
    } finally {
      setAdding(false);
    }
  };

  const openEditDialog = (item: InventoryWithProduct) => {
    setEditItem(item);
    setEditQuantity(formatQuantity(item.quantity));
    setEditCapacity(item.max_capacity ? formatQuantity(item.max_capacity) : '');
    setEditAlert(item.alert_threshold ? formatQuantity(item.alert_threshold) : '');
  };

  const closeEditDialog = () => {
    setEditItem(null);
    setEditQuantity('');
    setEditCapacity('');
    setEditAlert('');
  };

  const handleSaveEdit = async () => {
    if (!orgId || !stationId || !editItem || !editQuantity) return;

    setSaving(true);
    try {
      const result = await setInventory({
        path: { org_id: orgId, station_id: stationId },
        body: {
          product_id: editItem.product_id,
          quantity: editQuantity,
          max_capacity: editCapacity || null,
          alert_threshold: editAlert || null,
        },
      });

      if (result.error) {
        console.error('Failed to update inventory:', result.error);
        return;
      }

      await fetchInventory();
      closeEditDialog();
    } finally {
      setSaving(false);
    }
  };

  const adjustEditQuantity = (delta: number) => {
    const current = parseFloat(editQuantity) || 0;
    const newVal = Math.max(0, current + delta);
    setEditQuantity(newVal % 1 === 0 ? newVal.toString() : newVal.toFixed(2));
  };

  const handleDelete = async () => {
    if (!orgId || !stationId || !deleteItem) return;

    try {
      const result = await deleteInventory({
        path: { org_id: orgId, station_id: stationId, product_id: deleteItem.product_id },
      });

      if (result.error) {
        console.error('Failed to delete inventory item:', result.error);
        return;
      }

      await fetchInventory();
    } finally {
      setDeleteItem(null);
    }
  };

  if (!station) {
    return null;
  }

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">{t('inventory.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('inventory.manageStockLevels')}
            </p>
          </div>
          <Button onClick={() => setIsAddOpen(true)} disabled={availableProducts.length === 0}>
            <Plus className="w-4 h-4 mr-2" />
            {t('inventory.addProduct')}
          </Button>
        </div>

        {inventory.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">{t('inventory.noInventoryYet')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('inventory.addProductsToTrack')}
            </p>
            {availableProducts.length > 0 ? (
              <Button onClick={() => setIsAddOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('inventory.addProduct')}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('inventory.createProductsFirst')}
              </p>
            )}
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 md:px-4 py-3 text-sm font-medium">{t('common:labels.product')}</th>
                  <th className="text-right px-3 md:px-4 py-3 text-sm font-medium">{t('common:labels.price')}</th>
                  <th className="text-right px-3 md:px-4 py-3 text-sm font-medium">{t('common:labels.quantity')}</th>
                  <th className="w-20 text-right px-3 md:px-4 py-3 text-sm font-medium">{t('common:labels.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inventory.map((item) => {
                  const qty = parseFloat(item.quantity);
                  const maxCap = item.max_capacity ? parseFloat(item.max_capacity) : null;
                  const fillPct = maxCap && maxCap > 0 ? Math.min((qty / maxCap) * 100, 100) : null;
                  const isEmpty = qty <= 0;
                  const isLow = !isEmpty && !!item.alert_threshold && qty <= parseFloat(item.alert_threshold);

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => openEditDialog(item)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.product_name}</div>
                        {item.product_sku && (
                          <div className="text-xs text-muted-foreground">{item.product_sku}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatPrice(item.product_price, organization.currency) || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {fillPct !== null ? (
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  fillPct > 25
                                    ? 'bg-emerald-500'
                                    : fillPct > 10
                                      ? 'bg-amber-500'
                                      : 'bg-red-500'
                                }`}
                                style={{ width: `${fillPct}%` }}
                              />
                            </div>
                            <span className={`text-sm whitespace-nowrap ${isEmpty ? 'text-red-600 font-semibold' : isLow ? 'text-amber-600 font-medium' : ''}`}>
                              {formatQuantity(item.quantity)} / {formatQuantity(item.max_capacity!)} {item.product_unit}
                            </span>
                          </div>
                        ) : (
                          <span className={`text-sm ${isEmpty ? 'text-red-600 font-semibold' : isLow ? 'text-amber-600 font-medium' : ''}`}>
                            {formatQuantity(item.quantity)} {item.product_unit}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(item)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteItem(item)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Product Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('inventory.addToInventory')}</DialogTitle>
            <DialogDescription>
              {t('inventory.selectProductAndQuantity')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('common:labels.product')}</Label>
              {availableProducts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('inventory.allProductsInInventory')}
                </p>
              ) : (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {availableProducts.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setSelectedProduct(product)}
                      className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors ${
                        selectedProduct?.id === product.id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {product.sku && `${t('common:labels.sku')}: ${product.sku} • `}
                        {t('common:labels.unit')}: {product.unit}
                        {product.price && ` • ${formatPrice(product.price, organization.currency)}`}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedProduct && (
              <>
                <div className="space-y-2">
                  <Label>
                    {t('common:labels.quantity')} ({selectedProduct.unit})
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addQuantity}
                    onChange={(e) => setAddQuantity(e.target.value)}
                    placeholder={t('inventory.enterQuantity')}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t('inventory.maxCapacity')} ({selectedProduct.unit})
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addMaxCapacity}
                    onChange={(e) => setAddMaxCapacity(e.target.value)}
                    placeholder={t('inventory.optional')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t('inventory.alertThreshold')} ({selectedProduct.unit})
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addAlertThreshold}
                    onChange={(e) => setAddAlertThreshold(e.target.value)}
                    placeholder={t('inventory.optional')}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleAddProduct}
              disabled={!selectedProduct || !addQuantity || adding}
            >
              {adding ? t('inventory.adding') : t('inventory.addToInventory')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Inventory Dialog */}
      <Dialog open={!!editItem} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('inventory.editInventory')}</DialogTitle>
            <DialogDescription>
              {t('inventory.editInventoryDescription', { productName: editItem?.product_name })}
            </DialogDescription>
          </DialogHeader>

          {editItem && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('common:labels.quantity')} ({editItem.product_unit})</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => adjustEditQuantity(-1)}
                    disabled={parseFloat(editQuantity) <= 0}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editQuantity}
                    onChange={(e) => setEditQuantity(e.target.value)}
                    className="text-center"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => adjustEditQuantity(1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('inventory.maxCapacity')} ({editItem.product_unit})</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => {
                      const current = parseFloat(editCapacity) || 0;
                      const newVal = Math.max(0, current - 1);
                      setEditCapacity(newVal % 1 === 0 ? newVal.toString() : newVal.toFixed(2));
                    }}
                    disabled={!editCapacity || parseFloat(editCapacity) <= 0}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editCapacity}
                    onChange={(e) => setEditCapacity(e.target.value)}
                    className="text-center"
                    placeholder={t('inventory.optional')}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => {
                      const current = parseFloat(editCapacity) || 0;
                      const newVal = current + 1;
                      setEditCapacity(newVal % 1 === 0 ? newVal.toString() : newVal.toFixed(2));
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('inventory.alertThreshold')} ({editItem.product_unit})</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => {
                      const current = parseFloat(editAlert) || 0;
                      const newVal = Math.max(0, current - 1);
                      setEditAlert(newVal % 1 === 0 ? newVal.toString() : newVal.toFixed(2));
                    }}
                    disabled={!editAlert || parseFloat(editAlert) <= 0}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editAlert}
                    onChange={(e) => setEditAlert(e.target.value)}
                    className="text-center"
                    placeholder={t('inventory.optional')}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-9 w-9 p-0 shrink-0"
                    onClick={() => {
                      const current = parseFloat(editAlert) || 0;
                      const newVal = current + 1;
                      setEditAlert(newVal % 1 === 0 ? newVal.toString() : newVal.toFixed(2));
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              {t('common:buttons.cancel')}
            </Button>
            <Button onClick={handleSaveEdit} disabled={!editQuantity || saving}>
              {saving ? t('common:buttons.saving') : t('common:buttons.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inventory.removeFromInventory')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inventory.confirmRemoveInventory', { productName: deleteItem?.product_name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:buttons.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
